import { useEffect, useState, type ChangeEvent } from "react";
import { FileUp, Loader2, Save } from "lucide-react";
import {
  CertificationButton,
  CertificationInputField,
  CertificationModal,
  CertificationTextareaField,
} from "../../shared/ui";
import type {
  BibliotecaDocumento,
  BibliotecaMetadataPayload,
  BibliotecaTipo,
} from "../types/biblioteca";
import { bibliotecaTipoLabels } from "../types/biblioteca";

type Props = {
  open: boolean;
  tipo: BibliotecaTipo;
  document: BibliotecaDocumento | null;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onUpload: (file: File, payload: BibliotecaMetadataPayload) => void;
  onUpdate: (payload: BibliotecaMetadataPayload) => void;
};

const emptyPayload: BibliotecaMetadataPayload = {
  fecha_vigencia: "",
  observaciones: "",
};

export function BibliotecaDocumentModal({
  open,
  tipo,
  document,
  submitting,
  error,
  onClose,
  onUpload,
  onUpdate,
}: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [payload, setPayload] = useState<BibliotecaMetadataPayload>(emptyPayload);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setPayload({
      fecha_vigencia: document?.fecha_vigencia ?? "",
      observaciones: document?.observaciones ?? "",
    });
  }, [document, open]);

  const isEdit = document !== null;
  const canSubmit = isEdit || file !== null;
  const submit = () => {
    if (isEdit) {
      onUpdate(payload);
    } else if (file) {
      onUpload(file, payload);
    }
  };

  return (
    <CertificationModal
      open={open}
      title={isEdit ? "Editar datos del documento" : "Nuevo documento"}
      eyebrow={`Biblioteca · ${bibliotecaTipoLabels[tipo]}`}
      description={isEdit
        ? "Actualiza la vigencia y las observaciones sin sustituir el archivo."
        : "Sube un PDF y completa sus metadatos documentales."}
      onClose={onClose}
      onSubmit={submit}
      submitting={submitting}
      error={error}
      footer={
        <>
          <CertificationButton variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </CertificationButton>
          <CertificationButton
            variant="primary"
            type="submit"
            disabled={!canSubmit || submitting}
            icon={submitting
              ? <Loader2 className="animate-spin" size={16} />
              : isEdit
                ? <Save size={16} />
                : <FileUp size={16} />}
          >
            {submitting ? "Guardando..." : isEdit ? "Guardar" : "Subir documento"}
          </CertificationButton>
        </>
      }
    >
      <div className="iso27001-form-grid">
        {isEdit ? (
          <div className="biblioteca-readonly-field iso27001-field-full">
            <span>Nombre del documento</span>
            <strong>{document.nombre_documento}</strong>
          </div>
        ) : (
          <CertificationInputField
            fullWidth
            label="Archivo PDF"
            type="file"
            accept="application/pdf,.pdf"
            hint="Máximo 50 MB. El nombre se obtiene automáticamente del archivo."
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setFile(event.target.files?.[0] ?? null)}
          />
        )}

        <CertificationInputField
          fullWidth
          label="Fecha de vigencia"
          type="date"
          value={payload.fecha_vigencia}
          hint="Déjala vacía cuando el documento no tenga caducidad."
          onChange={(event) => setPayload((current) => ({
            ...current,
            fecha_vigencia: event.target.value,
          }))}
        />

        <CertificationTextareaField
          fullWidth
          label="Observaciones"
          rows={5}
          value={payload.observaciones}
          placeholder="Información relevante para la consulta del documento"
          onChange={(event) => setPayload((current) => ({
            ...current,
            observaciones: event.target.value,
          }))}
        />
      </div>
    </CertificationModal>
  );
}
